import { ContextMenuItemType } from 'data/types';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

type ContextMenuState = {
  visible: boolean;
  x: number;
  y: number;
  items: ContextMenuItemType[];
  sourceId?: string;
};

type ContextMenuContextType = {
  contextMenu: ContextMenuState;
  openContextMenu: (
    x: number,
    y: number,
    items: ContextMenuItemType[],
    sourceId?: string
  ) => void;
  updateContextMenuItems: (items: ContextMenuItemType[]) => void;
  closeContextMenu: () => void;
  isMenuOpenForSource: (sourceId: string) => boolean;
};

export const ContextMenuContext = createContext<ContextMenuContextType | null>(
  null
);

export function ContextMenuProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    items: [],
    sourceId: undefined
  });

  const openContextMenu = useCallback(
    (x: number, y: number, items: ContextMenuItemType[], sourceId?: string) => {
      setContextMenu({
        visible: true,
        x,
        y,
        items,
        sourceId
      });
    },
    []
  );

  const updateContextMenuItems = useCallback((items: ContextMenuItemType[]) => {
    setContextMenu((prev) => (prev.visible ? { ...prev, items } : prev));
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu((prev) => ({
      ...prev,
      visible: false,
      sourceId: undefined
    }));
  }, []);

  const isMenuOpenForSource = useCallback(
    (sourceId: string) => {
      return contextMenu.visible && contextMenu.sourceId === sourceId;
    },
    [contextMenu.visible, contextMenu.sourceId]
  );

  const contextMenuValue = useMemo(
    () => ({
      contextMenu,
      openContextMenu,
      updateContextMenuItems,
      closeContextMenu,
      isMenuOpenForSource
    }),
    [
      contextMenu,
      openContextMenu,
      updateContextMenuItems,
      closeContextMenu,
      isMenuOpenForSource
    ]
  );

  return (
    <ContextMenuContext.Provider value={contextMenuValue}>
      {children}
    </ContextMenuContext.Provider>
  );
}

export function useContextMenu() {
  const context = useContext(ContextMenuContext);
  if (!context) {
    throw new Error('useContextMenu must be used within a ContextMenuProvider');
  }
  return context;
}
